/**
 * העדפות נגישות — TASKS 4.6 · PRD 12.5.
 *
 * ⭐ **`localStorage`, ⛔ לא `sessionStorage`** — בשונה מהעגלה (`CartContext`,
 *    4.3), זה בכוונה: "שמירה ב-localStorage (העדפות נגישות בלבד — זה שימוש
 *    לגיטימי, בשונה מפריטי עגלה)" (TASKS 4.6). מבקר עם הגדלת טקסט צריך
 *    שההעדפה תישרוד גם סגירת דפדפן, ⛔ לא רק חזרה מטאב שנסגר בטעות.
 *
 * ⭐ המצב מוחל על `document.documentElement` (`<html>`) דרך `data-a11y-*` —
 *    כך ה-CSS (`index.css`) חל גלובלית בלי תלות באיפה ה-widget מותקן בעץ
 *    (`Layout.tsx`, רק בצד הציבורי — ⛔ פאנל הניהול טרם עבר סבב עיצוב, TASKS
 *    3.6/4.1). ⛔ לא לקבוע `font-size` בפיקסלים — ראו האזהרה ב-`index.css`
 *    (שוברת הגדלת טקסט של הדפדפן).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type A11yFontSize = 100 | 125 | 150 | 175;

export type A11yToggleKey =
  'highContrast' | 'grayscale' | 'emphasizeLinks' | 'readableFont' | 'stopAnimations';

export interface AccessibilityPrefs {
  fontSize: A11yFontSize;
  highContrast: boolean;
  grayscale: boolean;
  emphasizeLinks: boolean;
  readableFont: boolean;
  stopAnimations: boolean;
}

export const DEFAULT_A11Y_PREFS: AccessibilityPrefs = {
  fontSize: 100,
  highContrast: false,
  grayscale: false,
  emphasizeLinks: false,
  readableFont: false,
  stopAnimations: false,
};

const STORAGE_KEY = 'noam-a11y-prefs-v1';
const VALID_FONT_SIZES: readonly A11yFontSize[] = [100, 125, 150, 175];

interface AccessibilityContextValue {
  prefs: AccessibilityPrefs;
  setFontSize: (size: A11yFontSize) => void;
  toggle: (key: A11yToggleKey) => void;
  reset: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null);

/** ⛔ לעולם לא זורק — מיזוג שדה-שדה מול ברירת המחדל כדי לשרוד JSON פגום/חלקי. */
function loadFromLocalStorage(): AccessibilityPrefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_A11Y_PREFS;
    const parsed = JSON.parse(raw) as Partial<AccessibilityPrefs>;
    return {
      fontSize: VALID_FONT_SIZES.includes(parsed.fontSize as A11yFontSize)
        ? (parsed.fontSize as A11yFontSize)
        : DEFAULT_A11Y_PREFS.fontSize,
      highContrast: parsed.highContrast === true,
      grayscale: parsed.grayscale === true,
      emphasizeLinks: parsed.emphasizeLinks === true,
      readableFont: parsed.readableFont === true,
      stopAnimations: parsed.stopAnimations === true,
    };
  } catch {
    return DEFAULT_A11Y_PREFS;
  }
}

export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<AccessibilityPrefs>(() => loadFromLocalStorage());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // ⚠️ localStorage חסום/מלא (גלישה פרטית וכו') — ההעדפה עדיין פעילה
      // בסשן הנוכחי דרך ה-state, רק לא שורדת רענון. ⛔ לא קריטי.
    }

    const html = document.documentElement;
    if (prefs.fontSize === 100) html.removeAttribute('data-a11y-font-size');
    else html.setAttribute('data-a11y-font-size', String(prefs.fontSize));

    html.toggleAttribute('data-a11y-contrast-high', prefs.highContrast);
    html.toggleAttribute('data-a11y-grayscale', prefs.grayscale);
    html.toggleAttribute('data-a11y-emphasize-links', prefs.emphasizeLinks);
    html.toggleAttribute('data-a11y-readable-font', prefs.readableFont);
    html.toggleAttribute('data-a11y-stop-animations', prefs.stopAnimations);
  }, [prefs]);

  const setFontSize = useCallback((size: A11yFontSize) => {
    setPrefs((prev) => ({ ...prev, fontSize: size }));
  }, []);

  const toggle = useCallback((key: A11yToggleKey) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const reset = useCallback(() => setPrefs(DEFAULT_A11Y_PREFS), []);

  const value = useMemo(
    () => ({ prefs, setFontSize, toggle, reset }),
    [prefs, setFontSize, toggle, reset],
  );

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
}

export function useAccessibility(): AccessibilityContextValue {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error('useAccessibility חייב לרוץ בתוך AccessibilityProvider');
  return ctx;
}
